import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiClock, FiMapPin, FiArrowRight, FiCheck } from 'react-icons/fi';
import SEOPageLayout from '../components/SEOPageLayout';
import { tours, SITE_URL } from '../config/seo-data';

const ease = [0.22, 1, 0.36, 1];

const included = [
  'Mercedes-Benz with A/C',
  'English-speaking local driver',
  'Hotel or port pickup & drop-off',
  'Fully flexible itinerary',
  'Complimentary water & refreshments',
  'Photo stop opportunities',
];

const PrivateTours = () => (
  <SEOPageLayout
    title="Lasmari Chauffeurs | Corfu Private Tours & Sightseeing"
    description="Explore Corfu with a private driver. Custom island tours, Achilleion Palace, Paleokastritsa, Old Town UNESCO site, wine tasting, and beach tours. Half-day and full-day."
    keywords="corfu private tour, corfu island tour, corfu sightseeing tour, corfu day tour, corfu shore excursion, corfu Achilleion Palace tour, corfu highlights tour"
    canonical={`${SITE_URL}/private-tours`}
  >
    {/* Hero */}
    <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('/images/backgrounds/tour-vclass-hero.webp')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/80" />
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse 65% 55% at 50% 45%, rgba(0,0,0,0.55), transparent 72%)' }}
        />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease }}
          className="text-white/85 text-xs uppercase tracking-[0.4em] mb-6 font-light"
          style={{ textShadow: '0 1px 3px rgba(0,0,0,0.85), 0 2px 16px rgba(0,0,0,0.6)' }}
        >
          Customised Island Experiences
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease, delay: 0.1 }}
          className="text-5xl md:text-6xl lg:text-7xl text-white mb-8 leading-[1.08] tracking-tight"
          style={{ fontFamily: "'Playfair Display', serif", textShadow: '0 1px 3px rgba(0,0,0,0.85), 0 2px 18px rgba(0,0,0,0.6)' }}
        >
          Private Corfu
          <br />
          <span className="font-light italic">Tours</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease, delay: 0.2 }}
          className="text-gray-300 text-lg lg:text-xl max-w-2xl mx-auto mb-10 font-light leading-relaxed"
          style={{ textShadow: '0 1px 3px rgba(0,0,0,0.85), 0 2px 16px rgba(0,0,0,0.6)' }}
        >
          Discover the best of Corfu with your own driver. Custom itineraries, a comfortable Mercedes-Benz, and a local who knows every hidden gem.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease, delay: 0.3 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <a
            href="https://wa.me/447404560313?text=Hello!%20I%27d%20like%20to%20inquire%20about%20a%20private%20tour."
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#ba1609] text-white px-10 py-4 rounded-full font-medium hover:bg-[#8e1107] transition-all duration-300 text-base tracking-wide"
          >
            Request a Quote
          </a>
          <a
            href="#tours"
            className="text-white/80 hover:text-white px-8 py-4 rounded-full border border-white/20 hover:border-white/40 transition-all duration-300 text-sm tracking-wide"
          >
            View Tours
          </a>
        </motion.div>
      </div>
    </section>

    {/* What's Included */}
    <section className="py-24 lg:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease }}
          >
            <p className="text-[#ba1609] text-xs uppercase tracking-[0.4em] mb-4">Every Tour Includes</p>
            <h2 className="text-3xl lg:text-4xl font-light text-[#1a1012] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
              A Truly
              <br /><span className="italic">Personal Experience</span>
            </h2>
            <div className="space-y-4">
              {included.map((item, i) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06, ease }}
                  className="flex items-center gap-3"
                >
                  <div className="w-5 h-5 rounded-full bg-[#fbeceb] border border-[#ba1609]/20 flex items-center justify-center shrink-0">
                    <FiCheck className="w-3 h-3 text-[#ba1609]" />
                  </div>
                  <span className="text-[#5b4d4f] text-sm font-light">{item}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease }}
            className="bg-[#faf7f6] border border-black/10 rounded-2xl p-8 lg:p-10 shadow-sm"
          >
            <h3 className="text-[#1a1012] text-xl font-medium mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Why a Private Tour?
            </h3>
            <div className="w-10 h-[1px] bg-[#ba1609] mb-6" />
            <div className="space-y-4 text-[#5b4d4f] text-sm leading-relaxed font-light">
              <p>Corfu's most beautiful spots aren't on the bus routes. With a private driver, you visit hidden beaches, mountain villages, and panoramic viewpoints that group tours skip.</p>
              <p>Your driver is a local, they know which taverna has the freshest fish, which beach is quiet at midday, and the back roads that avoid summer traffic.</p>
              <p>Stop whenever you want, stay as long as you like, and change the plan on the fly. It's your day.</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>

    {/* Tour Cards */}
    <section id="tours" className="py-24 lg:py-32 bg-[#faf7f6]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease }}
          className="text-center mb-16"
        >
          <p className="text-[#ba1609] text-xs uppercase tracking-[0.4em] mb-4">Our Tours</p>
          <h2 className="text-3xl lg:text-5xl font-light text-[#1a1012]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Curated Experiences
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-6">
          {tours.map((tour, i) => (
            <motion.div
              key={tour.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1, ease }}
            >
              <Link
                to={`/tours/${tour.slug}`}
                className="group block bg-white border border-black/10 rounded-2xl p-8 shadow-sm hover:shadow-lg hover:-translate-y-1 hover:border-[#ba1609]/30 transition-all duration-500 h-full"
              >
                <div className="flex items-center gap-3 mb-5">
                  <span className="bg-[#fbeceb] text-[#ba1609] text-[10px] px-3 py-1 rounded-full uppercase tracking-wider border border-[#ba1609]/20">
                    {tour.type}
                  </span>
                  <span className="text-[#5b4d4f] text-xs flex items-center gap-1">
                    <FiClock className="w-3 h-3" /> {tour.duration}
                  </span>
                </div>

                <h3 className="text-2xl text-[#1a1012] font-medium mb-3 group-hover:text-[#ba1609] transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {tour.name}
                </h3>

                <p className="text-[#5b4d4f] text-sm mb-6 leading-relaxed font-light line-clamp-2">
                  {tour.description}
                </p>

                <div className="flex flex-wrap gap-2 mb-6">
                  {tour.stops.slice(0, 4).map((stop) => (
                    <span key={stop} className="text-xs bg-[#faf7f6] border border-black/10 px-3 py-1.5 rounded-lg text-[#5b4d4f]">
                      <FiMapPin className="w-3 h-3 inline mr-1 text-[#ba1609]" />{stop}
                    </span>
                  ))}
                  {tour.stops.length > 4 && (
                    <span className="text-xs text-[#5b4d4f]">+{tour.stops.length - 4} more</span>
                  )}
                </div>

                <div className="flex justify-between items-center pt-4 border-t border-black/10">
                  <span className="text-[#ba1609] text-sm font-light">Contact for personalised quote</span>
                  <FiArrowRight className="w-4 h-4 text-[#5b4d4f] group-hover:text-[#ba1609] group-hover:translate-x-1 transition-all duration-300" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  </SEOPageLayout>
);

export default PrivateTours;
